import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Import child components
import RevenueExpenseSection from '@components/Financials/RevenueExpenseSection';
import KpiSection from '@components/Financials/KpiSection';
import { Metric, HotelFinancialsPageProps } from '@types';
import { api } from '@services';


const HotelFinancialsPage: React.FC<HotelFinancialsPageProps> = ({ hotelId, initialData = [], yearsToShow }) => {
  const navigate = useNavigate();
  // State for the two sections
  const [pnlItems, setPnlItems] = useState<Metric[]>([]);
  const [kpiItems, setKpiItems] = useState<Metric[]>([]);
  // Loading and error state for the save operation
  const [isLoading, setIsLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hotelName, setHotelName] = useState<string>('');

  // Fetch hotel details and financials when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setFetchLoading(true);
      setError(null);

      try {
        // Fetch hotel details to get the name
        const hotelResponse = await api.get(`/hotels/${hotelId}`);
        setHotelName(hotelResponse.data.name || `Hotel #${hotelId}`);

        // Fetch confirmed financials
        const financialsResponse = await api.get(`/hotels/${hotelId}/confirmed-financials`);
        console.log('Fetched hotel financials:', financialsResponse.data);

        // Process the financial data into the expected format
        const financials = financialsResponse.data || [];
        const processedData = financials.map((item: any, index: number) => ({
          category: item.category,
          item: item.category, // Use category as item name for simplicity
          year: parseInt(item.year),
          value: parseFloat(item.value) || 0,
          tempId: `${item.category}-${item.year}-${index}`,
        }));

        // Split into P&L and KPI categories
        const kpiCategories = ['Occupancy Rate', 'ADR', 'RevPAR'];
        const processedPnl = processedData.filter((item: Metric) => !kpiCategories.includes(item.category));
        const processedKpi = processedData.filter((item: Metric) => kpiCategories.includes(item.category));

        setPnlItems(processedPnl);
        setKpiItems(processedKpi);
      } catch (err: any) {
        console.error('Error fetching financial data:', err);
        setError('Failed to load financial data. Please try again.');
      } finally {
        setFetchLoading(false);
      }
    };

    if (hotelId) {
      fetchData();
    }
  }, [hotelId]);

  // --- Save Handler ---
  const handleSaveFinancials = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // Combine data from both sections
    // Map to the expected format for the API
    const itemsToSave = [...pnlItems, ...kpiItems]
      .filter(item => item.value !== undefined && !isNaN(Number(item.value)))
      .map((item) => ({
        category: item.category,
        year: item.year,
        value: Number(item.value) || 0,
      }));

    console.log('Saving financials:', itemsToSave);

    try {
      // Use the batch financials API endpoint
      const response = await api.post(`/hotels/${hotelId}/financials/batch`, {
        items: itemsToSave
      });

      console.log('Save response:', response.data);
      setSuccess('Financial data saved successfully!');

      // Show success message for 3 seconds then navigate back
      setTimeout(() => {
        navigate(`/hotels/${hotelId}`);
      }, 3000);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err.message || 'Failed to save financials.';
      setError(errorMessage);
      console.error('Save Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/hotels/${hotelId}`);
  };

  if (fetchLoading) {
    return (
      <div className="container mx-auto p-4 flex justify-center items-center min-h-[300px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p>Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Financials for {hotelName}</h2>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Back to Hotel
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
          {success}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-lg font-semibold mb-4">Financial Data Entry</h3>
        <p className="text-gray-600 mb-4">
          Enter the financial metrics for your hotel. All values should be entered as numbers.
          For percentages (like occupancy rate), enter the actual percentage (e.g., 75 for 75%).
        </p>
      </div>

      {/* Render the separate sections, passing down relevant items and setters */}
      <RevenueExpenseSection
        items={pnlItems}
        years={yearsToShow}
        onChange={setPnlItems}
        disabled={isLoading}
      />

      <KpiSection
        items={kpiItems}
        years={yearsToShow}
        onChange={setKpiItems}
        disabled={isLoading}
      />

      {/* Save Button */}
      <div className="mt-6 text-right">
        <button
          onClick={handleSaveFinancials}
          disabled={isLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ml-auto"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            'Save All Financials'
          )}
        </button>
      </div>
    </div>
  );
};

export default HotelFinancialsPage;
